import React from 'react';
import { motion } from 'framer-motion';
import {
  File,
  Image,
  FileText,
  Download,
  Eye,
  X
} from 'lucide-react';

interface Attachment {
  id: number;
  filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  comment_id?: number;
  created_at: string;
  updated_at: string;
  url?: string;
  formatted_size?: string;
  isImage?: boolean;
  isPdf?: boolean;
  isDocument?: boolean;
}

interface AttachmentPreviewProps {
  attachment: Attachment;
  onRemove?: () => void;
  showRemoveButton?: boolean;
  className?: string;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachment,
  onRemove,
  showRemoveButton = false,
  className = ''
}) => {
  const getFileIcon = () => {
    if (attachment.isImage) {
      return <Image className="w-5 h-5 text-blue-600" />;
    } else if (attachment.isPdf) {
      return <FileText className="w-5 h-5 text-red-600" />;
    } else {
      return <File className="w-5 h-5 text-gray-600" />;
    }
  };

  const handlePreview = () => {
    if (attachment.isImage) {
      window.open(attachment.url, '_blank');
    } else if (attachment.isPdf) {
      window.open(attachment.url, '_blank');
    } else {
      // For other files, trigger download
      const link = document.createElement('a');
      link.href = attachment.url || '';
      link.download = attachment.filename;
      link.click();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = attachment.url || '';
    link.download = attachment.filename;
    link.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`relative bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow ${className}`}
    >
      <div className="flex items-center gap-3">
        {/* File Icon */}
        <div className="flex-shrink-0">
          {getFileIcon()}
        </div>

        {/* File Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {attachment.filename}
          </p>
          <p className="text-xs text-gray-500">
            {attachment.formatted_size}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {attachment.isImage || attachment.isPdf ? (
            <button
              onClick={handlePreview}
              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Preview"
            >
              <Eye className="w-4 h-4" />
            </button>
          ) : null}

          <button
            onClick={handleDownload}
            className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>

          {showRemoveButton && onRemove && (
            <button
              onClick={onRemove}
              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Image Preview */}
      {attachment.isImage && (
        <div className="mt-3">
          <img
            src={attachment.url}
            alt={attachment.filename}
            className="h-32 object-cover rounded-md cursor-pointer hover:opacity-90 transition-opacity"
            onClick={handlePreview}
          />
        </div>
      )}
    </motion.div>
  );
};

export default AttachmentPreview;
